import type { ProposalJSON } from "work/proposals/_schemas/proposal-schema";
import type { ProposalReviewerJSON } from "work/proposals/reviewers/_schemas/proposal-reviewer-schema";
import { ProposalStatus } from "work/proposals/__constants/proposal-lifecycle";
import { IS_DEV } from "_shared/_utilities/meta";
import AHDate from "_shared/dates/date";
import type {
  ApproveProposalNegotiationCommand,
  CancelProposalNegotiationCommand,
  CompleteProposalReviewCommand,
  HireProposalNegotiationCommand,
  ProposalCollaborationCapabilities,
  ProposalCollaborationEnvelope,
  ProposalContentOperation,
  ProposalReviewerChange,
  RequestProposalReviewCommand,
  RequestProposalRevisionNegotiationCommand,
  RejectProposalNegotiationCommand,
  SaveProposalDraftCommand,
  SubmitProposalDraftCommand,
} from "work/proposals/negotiation/proposal-collaboration-contract";
import { getProposalCollectionItemIdentity } from "work/proposals/negotiation/proposal-operation-identity";
import { readProposalOperationValue } from "work/proposals/negotiation/proposal-operation-projector";
import {
  canonicalProposalCollectionItem,
  canonicalProposalCollectionItemForCommand,
  proposalStrictJsonValuesEqual,
} from "work/proposals/negotiation/proposal-collection-codec";

type IdFactory = () => string;
type ProposalJSONContentOperation = Exclude<
  ProposalContentOperation,
  { kind: "ResolutionDecision" }
>;

const editableSubmittedFieldPaths = [
  "/name",
  "/description",
  "/responseDueBy",
  "/startDate",
  "/endDate",
  "/teamRestricted/value",
  "/discount/percent",
  "/conflictsCheckWaived/value",
] as const;

const editableDraftSetupFieldPaths = [
  ...editableSubmittedFieldPaths,
  "/negotiable",
  // Client Representative fields remain only for initial Details setup. The Vendor Team is the
  // negotiable team; its identity is setup data, while its roster/templates remain proposal content.
  // Client Team membership is reference state and is deliberately absent from both content lists.
  "/clientTeam/isMasked",
  "/clientTeam/leader",
  "/clientTeam/marketplaceId",
  "/vendorTeam/isMasked",
  "/vendorTeam/leader",
  "/vendorTeam/marketplaceId",
] as const;

/**
 * For a scalar, absent is absent: the projection serializes "not set" as null while the editor
 * round-trips it as undefined, and `Object.is(null, undefined)` is false. Coalescing before diffing is
 * what stops a save from inventing a change to a field nobody touched.
 *
 * This was once scoped to the two team `marketplaceId` paths, on the belief that other scalars
 * "deliberately keep the null-vs-undefined distinction". They do not, and the cost was not cosmetic: an
 * unset Response Due By emitted a phantom `FieldSet` on EVERY save, so the confirmation announced
 * "Response Due By Cleared" for a field that had never held a value, a real content event went into the
 * stream, and every reviewer got a change notification on a field the author had not edited. Clearing a
 * field that DID hold a value is untouched — the old value still differs from absent, so that still
 * emits its operation.
 */
function coalesceAbsentScalar(value: unknown): unknown {
  return value ?? undefined;
}

const editableCalendarDateFieldPaths: ReadonlySet<string> = new Set([
  "/responseDueBy",
  "/startDate",
  "/endDate",
]);

/**
 * Proposal dates are calendar-day values even though the wire stores ISO datetimes. The picker writes
 * the end of the selected day, while a projection can carry a different time or fractional precision
 * for that same displayed date. Comparing those strings literally makes an already-saved date look
 * changed again during the next editor reconciliation, putting the same change back in the durable
 * queue and Save confirmation.
 *
 * Null remains distinct from a date, and different calendar days remain real changes.
 */
function proposalScalarValuesEqual(path: string, expectedValue: unknown, value: unknown): boolean {
  if (proposalStrictJsonValuesEqual(expectedValue, value)) return true;
  if (!editableCalendarDateFieldPaths.has(path) || expectedValue == null || value == null) return false;

  const expectedDate = AHDate.fromJSON(expectedValue);
  const date = AHDate.fromJSON(value);
  return Boolean(expectedDate?.isValid && date?.isValid && expectedDate.isSame(date, "day"));
}

const editableCollectionPaths = [
  "/feeSchedule/categories",
  "/clientPolicies/documents",
  "/vendorPolicies/documents",
  "/conflicts/documents",
  "/vendorTeam/members",
  // Both parties' team-template selections belong here, exactly as both parties' fee-schedule
  // template selections do. `clientTeamTemplateIds` records which team templates the CLIENT applied
  // while composing the negotiable Vendor Team -- it is not client-team membership, which is
  // reference state carried by the client-team-members command (see
  // docs/design/proposals-negotiation/ARCHITECTURE.md). Omitting it meant a client's template
  // checkbox never reached the wire: the members it added were saved through /vendorTeam/members
  // while the selection that owned them was dropped, so the box came back unticked over members that
  // were still there, and the next click added them again instead of taking them back (#4486).
  "/clientTeamTemplateIds",
  "/vendorTeamTemplateIds",
  "/clientFeeScheduleTemplateIds",
  "/vendorFeeScheduleTemplateIds",
] as const;

const identityProperties = new Set(["id", "documentId", "userId", "legalEntityId", "categoryId", "memberId"]);

/**
 * Paths the server accepts only for initial proposal setup before first submission. This includes
 * selecting the negotiable Vendor Team, not editing Client Team membership. Derived from the two
 * editable sets so the two can never drift apart.
 */
const initialSetupOnlyFieldPaths: ReadonlySet<string> = new Set(
  editableDraftSetupFieldPaths.filter(
    (path) => !(editableSubmittedFieldPaths as readonly string[]).includes(path),
  ),
);

/**
 * Whether this actor may edit initial-setup-only paths. An unsubmitted status is necessary but not
 * sufficient — a reviewer holding edit rights on a draft still may not touch them — so the answer
 * comes from the server-supplied capability, never from status alone. A capability object that
 * predates the flag resolves to false, which is the safe direction: the actor keeps editing every
 * ordinary field and simply stops emitting operations the server would reject with a 403.
 */
function canEditInitialSetupPaths(capabilities: ProposalCollaborationCapabilities | undefined): boolean {
  return (capabilities?.canConfigureInitialProposalSetup ?? capabilities?.canSelectInitialTeams) === true;
}

/**
 * Whether the server has DEFINITELY told this actor it may not configure initial proposal setup —
 * the negotiable Vendor Team's identity (leader, marketplace team, masking) and negotiability.
 *
 * This is the deliberately tolerant counterpart of the send-path check above, and the two answer
 * different questions. Deciding what to put on the wire, silence must mean "no"; deciding what to
 * OFFER, silence must mean "carry on", because a rolling deployment that omits both flags would
 * otherwise take the leader control away from the creator who legitimately holds it.
 *
 * Use this to gate the affordances that emit initial-setup operations. A reviewer holding
 * `canEditDraft` may change the Vendor Team's ROSTER but not its IDENTITY, so offering them a
 * leader selector produced an edit the send-path filter then dropped without a word: the change
 * appeared to take, the save reported success, and the leader was unchanged on the next read
 * (#4854).
 */
export function isBarredFromInitialProposalSetup(
  capabilities: ProposalCollaborationCapabilities | undefined,
): boolean {
  return (capabilities?.canConfigureInitialProposalSetup ?? capabilities?.canSelectInitialTeams) === false;
}

export function buildProposalContentOperations(
  original: ProposalJSON,
  updated: ProposalJSON,
  createId: IdFactory = () => crypto.randomUUID(),
  capabilities?: ProposalCollaborationCapabilities,
): ProposalJSONContentOperation[] {
  const operations: ProposalJSONContentOperation[] = [];

  // Callers that have not yet threaded the envelope's capabilities through fall back to the
  // historical status-only choice; `buildSaveProposalDraftCommand` re-checks the capability before
  // anything reaches the wire, so an unauthorized path can never actually be sent.
  const canEditSetupPaths = capabilities === undefined
    ? original.status === ProposalStatus.AwaitingSubmission
    : canEditInitialSetupPaths(capabilities);
  const fieldPaths = canEditSetupPaths ? editableDraftSetupFieldPaths : editableSubmittedFieldPaths;
  for (const path of fieldPaths) {
    const expectedValue = coalesceAbsentScalar(readProposalOperationValue(original, path));
    const value = coalesceAbsentScalar(readProposalOperationValue(updated, path));
    if (proposalScalarValuesEqual(path, expectedValue, value)) continue;
    operations.push({
      operationId: createId(),
      kind: "FieldSet",
      path,
      conflictKey: path.slice(1).replaceAll("/", ":"),
      expectedValue: cloneJson(expectedValue),
      value: cloneJson(value),
    });
  }

  for (const path of editableCollectionPaths) {
    operations.push(...buildCollectionOperations(original, updated, path, createId));
  }

  return operations;
}

export function buildProposalReviewerChanges(
  original: ProposalJSON,
  updated: ProposalJSON,
  party: ProposalCollaborationEnvelope["viewingParty"],
): ProposalReviewerChange[] {
  const originalReviewers = party === "Client" ? original.clientReviewers : original.vendorReviewers;
  const updatedReviewers = party === "Client" ? updated.clientReviewers : updated.vendorReviewers;
  const originalById = new Map(originalReviewers.map((reviewer) => [reviewerIdentity(reviewer), reviewer]));
  const updatedById = new Map(updatedReviewers.map((reviewer) => [reviewerIdentity(reviewer), reviewer]));
  const changes: ProposalReviewerChange[] = [];

  for (const reviewer of updatedReviewers) {
    const originalReviewer = originalById.get(reviewerIdentity(reviewer));
    if (!originalReviewer) {
      changes.push({
        kind: "Assign",
        reviewerUserId: reviewer.userId,
        reviewerLegalEntityId: reviewer.legalEntityId,
        canEdit: reviewer.isEditing,
        customMessage: reviewer.customMessage ?? null,
      });
    } else if (originalReviewer.isEditing !== reviewer.isEditing) {
      changes.push({
        kind: "SetCanEdit",
        reviewerUserId: reviewer.userId,
        reviewerLegalEntityId: reviewer.legalEntityId,
        expectedCanEdit: originalReviewer.isEditing,
        canEdit: reviewer.isEditing,
      });
    }
    if (originalReviewer && originalReviewer.customMessage !== reviewer.customMessage) {
      changes.push({
        kind: "SetCustomMessage",
        reviewerUserId: reviewer.userId,
        reviewerLegalEntityId: reviewer.legalEntityId,
        expectedCustomMessage: originalReviewer.customMessage ?? null,
        customMessage: reviewer.customMessage ?? null,
      });
    }
  }

  for (const reviewer of originalReviewers) {
    if (updatedById.has(reviewerIdentity(reviewer))) continue;
    changes.push({
      kind: "Remove",
      reviewerUserId: reviewer.userId,
      reviewerLegalEntityId: reviewer.legalEntityId,
      expectedCanEdit: reviewer.isEditing,
      expectedCustomMessage: reviewer.customMessage ?? null,
    });
  }

  return changes;
}

/**
 * Whether this actor may put any proposal-content operation on the wire at all. The server's save
 * guard reads `operations.Count > 0 && !CanEditDraft` — every kind, `ResolutionDecision` included — so
 * for a comment-only reviewer the authorized set is empty, not merely narrowed (#4535). Unlike
 * `canConfigureInitialProposalSetup`, `canEditDraft` is a required field of the capabilities contract,
 * so a `false` here is a definite answer from the server rather than a rolling-deployment silence.
 */
export function canSendProposalContentOperations(
  capabilities: ProposalCollaborationCapabilities | undefined,
): boolean {
  return capabilities?.canEditDraft !== false;
}

/**
 * Drops operations this actor is not permitted to send. The server rejects the whole command with a
 * 403 if it carries even one initial-setup-only path the actor cannot write, so a single such
 * operation would otherwise block every unrelated edit in the same save.
 */
export function filterUnauthorizedProposalContentOperations(
  operations: ProposalContentOperation[],
  capabilities: ProposalCollaborationCapabilities | undefined,
): ProposalContentOperation[] {
  if (!canSendProposalContentOperations(capabilities)) return [];
  if (canEditInitialSetupPaths(capabilities)) return operations;
  return operations.filter(
    (operation) => operation.kind === "ResolutionDecision" || !initialSetupOnlyFieldPaths.has(operation.path),
  );
}

/**
 * The reviewer-roster counterpart. The server rejects the whole command when it carries any reviewer
 * change from an actor without `canManageReviewers`, so a roster change that reached the wire from a
 * reviewer took every unrelated edit in the same save down with it — as a bare 403 with no body, which
 * the app could only report as an unexplained failure (#4535).
 */
export function filterUnauthorizedProposalReviewerChanges(
  reviewerChanges: ProposalReviewerChange[],
  capabilities: ProposalCollaborationCapabilities | undefined,
): ProposalReviewerChange[] {
  return capabilities?.canManageReviewers === false ? [] : reviewerChanges;
}

/** Document collections (client/vendor policies, conflicts) carry display metadata locally. */
function isDocumentCollectionPath(path: string): boolean {
  return path.endsWith("/documents");
}

/**
 * A document CollectionItemAdded op is projected into the local proposal display-complete (it keeps
 * its `documentInfo` so the list renders immediately), but the persisted/replayed command must stay
 * semantic — display metadata never crosses the wire. Re-canonicalize the add value here, at the
 * single wire boundary, so the command is byte-stable for idempotency and replay. Non-document
 * collections are already canonical in the op and pass through untouched.
 */
function canonicalizeProposalContentOperationForWire(
  operation: ProposalContentOperation,
): ProposalContentOperation {
  if (operation.kind === "CollectionItemAdded") {
    if (!isDocumentCollectionPath(operation.path) && operation.path !== "/feeSchedule/categories") return operation;
    return {
      ...operation,
      value: canonicalProposalCollectionItemForCommand(operation.path, operation.value) as never,
    };
  }
  if (operation.path !== "/feeSchedule/categories") return operation;
  if (operation.kind === "CollectionItemUpdated") {
    return {
      ...operation,
      expectedValue: canonicalProposalCollectionItem(operation.path, operation.expectedValue) as never,
      value: canonicalProposalCollectionItemForCommand(operation.path, operation.value) as never,
    };
  }
  if (operation.kind === "CollectionItemRemoved") {
    return {
      ...operation,
      expectedValue: canonicalProposalCollectionItem(operation.path, operation.expectedValue) as never,
    };
  }
  return operation;
}

/**
 * An operation's values reduced to what its request body carries. A FieldSet copies editor JSON as it
 * stands, and `EntityMemberSchema.serialize` writes an absent `replacesId` as an own `undefined` — so a
 * chosen team's `/vendorTeam/leader` held a key that `JSON.stringify` drops and `z.json()` refuses.
 * `saveProposalCollaborationDraft` parses the command against `SaveProposalDraftCommandZ` before sending
 * it, and that refusal kept every save that chose a team from ever leaving the browser. Serializing
 * here keeps the command identical to the body the server receives, including for operations queued
 * before this ran.
 */
function proposalContentOperationAsJson(operation: ProposalContentOperation): ProposalContentOperation {
  return JSON.parse(JSON.stringify(operation)) as ProposalContentOperation;
}

export function buildSaveProposalDraftCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  operations: ProposalContentOperation[];
  reviewerChanges: ProposalReviewerChange[];
  createId?: IdFactory;
}): SaveProposalDraftCommand {
  const operations = filterUnauthorizedProposalContentOperations(
    params.operations.map(canonicalizeProposalContentOperationForWire).map(proposalContentOperationAsJson),
    params.envelope.capabilities,
  );
  const reviewerChanges = filterUnauthorizedProposalReviewerChanges(
    params.reviewerChanges,
    params.envelope.capabilities,
  );
  logProposalSaveOperations(operations);

  return {
    commandId: (params.createId ?? (() => crypto.randomUUID()))(),
    expectedSubmissionId: params.envelope.currentSubmissionId,
    expectedStreamVersion: params.envelope.streamVersion,
    expectedContentVersion: params.envelope.contentVersion,
    operations,
    reviewerChanges,
  };
}

/**
 * Development-only trace of exactly what a save is about to persist, at the single wire boundary every
 * save passes through.
 *
 * Every operation here becomes a durable content event, and every content event becomes a change
 * notification on the recipients' badges. A save that carries a field nobody edited is therefore not a
 * cosmetic problem — it is how a reviewer handed one fee-category change ends up with a notification on
 * Details, Team and Discount as well. When that is reported, this table answers the only question that
 * matters — which paths went out, and what each one claims to have changed from and to — without
 * guessing from the badges backwards.
 */
function logProposalSaveOperations(operations: readonly ProposalContentOperation[]): void {
  if (!IS_DEV || operations.length === 0) return;
  console.debug(
    `[proposal save] sending ${operations.length} operation(s)`,
    operations.map((operation) => ({
      kind: operation.kind,
      path: operation.path,
      itemId: "itemId" in operation ? operation.itemId : undefined,
      from: "expectedValue" in operation ? operation.expectedValue : undefined,
      to: "value" in operation ? operation.value : undefined,
    })),
  );
}

/**
 * Submission is cursor-only: content and reviewer changes are saved first, then
 * the resulting authoritative envelope supplies every optimistic-concurrency
 * value. Keeping this builder envelope-based prevents a page render closure
 * from accidentally submitting an older checkpoint.
 */
export function buildSubmitProposalDraftCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  createId?: IdFactory;
}): SubmitProposalDraftCommand {
  return {
    commandId: (params.createId ?? (() => crypto.randomUUID()))(),
    expectedSubmissionId: params.envelope.currentSubmissionId,
    expectedStreamVersion: params.envelope.streamVersion,
    expectedContentVersion: params.envelope.contentVersion,
  };
}

/**
 * A review endorses an exact submitted checkpoint. Build it from the latest
 * authoritative envelope after pending edits have been saved so the approval
 * can never accidentally describe an older version of the proposal.
 */
export function buildCompleteProposalReviewCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  createId?: IdFactory;
}): CompleteProposalReviewCommand {
  return {
    commandId: (params.createId ?? (() => crypto.randomUUID()))(),
    expectedSubmissionId: params.envelope.currentSubmissionId,
    expectedStreamVersion: params.envelope.streamVersion,
    expectedContentVersion: params.envelope.contentVersion,
  };
}
/**
 * A re-review request dismisses one reviewer's approval. It is built against the same
 * authoritative envelope a review is, so a leader can never dismiss an approval described by an
 * older version of the proposal than the one they are looking at.
 */
export function buildRequestProposalReviewCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  reviewerUserId: string;
  reviewerLegalEntityId: string;
  createId?: IdFactory;
}): RequestProposalReviewCommand {
  return {
    commandId: (params.createId ?? (() => crypto.randomUUID()))(),
    expectedSubmissionId: params.envelope.currentSubmissionId,
    expectedStreamVersion: params.envelope.streamVersion,
    expectedContentVersion: params.envelope.contentVersion,
    reviewerUserId: params.reviewerUserId,
    reviewerLegalEntityId: params.reviewerLegalEntityId,
  };
}


/**
 * Request revision publishes an active party workspace against the same exact cursor contract as
 * submission, while retaining its distinct lifecycle meaning in the command receipt and event stream.
 */
export function buildRequestProposalRevisionNegotiationCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  createId?: IdFactory;
}): RequestProposalRevisionNegotiationCommand {
  return buildSubmitProposalDraftCommand(params);
}

/** A party approval accepts the exact canonical projection visible after pending work is saved. */
export function buildApproveProposalNegotiationCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  createId?: IdFactory;
}): ApproveProposalNegotiationCommand {
  return {
    commandId: (params.createId ?? (() => crypto.randomUUID()))(),
    expectedSubmissionId: params.envelope.currentSubmissionId,
    expectedStreamVersion: params.envelope.streamVersion,
    expectedContentVersion: params.envelope.contentVersion,
  };
}

/** Reject the exact submitted negotiation projection the actor is currently viewing. */
export function buildRejectProposalNegotiationCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  createId?: IdFactory;
}): RejectProposalNegotiationCommand {
  return buildApproveProposalNegotiationCommand(params);
}

/** Cancel the exact submitted negotiation projection the actor is currently viewing. */
export function buildCancelProposalNegotiationCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  createId?: IdFactory;
}): CancelProposalNegotiationCommand {
  return buildApproveProposalNegotiationCommand(params);
}

/** Hire from the exact accepted canonical projection that will become the immutable agreement. */
export function buildHireProposalNegotiationCommand(params: {
  envelope: ProposalCollaborationEnvelope;
  createId?: IdFactory;
}): HireProposalNegotiationCommand {
  return buildApproveProposalNegotiationCommand(params);
}

/**
 * Reads a collection, treating an absent parent or collection as empty. Returns null for a
 * value that exists but is not a collection, which is not something to diff.
 */
function readCollection(proposal: ProposalJSON, path: string): unknown[] | null {
  const value = readProposalOperationValue(proposal, path);
  if (value === undefined || value === null) return [];
  return Array.isArray(value) ? value : null;
}

function buildCollectionOperations(
  original: ProposalJSON,
  updated: ProposalJSON,
  path: string,
  createId: IdFactory,
): ProposalJSONContentOperation[] {
  // A collection's parent can be absent — a proposal has no vendor team until one is
  // chosen — which reads as an empty roster rather than an unchanged one. Without this,
  // choosing a team for the first time produced no operations and was silently dropped.
  const originalCollection = readCollection(original, path)?.map((item) =>
    canonicalProposalCollectionItemForCommand(path, item),
  );
  const updatedCollection = readCollection(updated, path)?.map((item) =>
    canonicalProposalCollectionItemForCommand(path, item),
  );
  if (!originalCollection || !updatedCollection) return [];

  const originalById = collectionById(originalCollection, path);
  const updatedById = collectionById(updatedCollection, path);
  // The diff runs on canonical items (so display-only metadata never produces spurious update ops),
  // but a newly-added item's value is projected straight into the local effective proposal. Documents
  // carry their display data in a separate `documentInfo` blob that canonicalization strips and the
  // deserializer then requires, so a canonical add value renders as the empty-list placeholder until a
  // server round-trip. For document collections only, seed the add value from the raw (display-complete)
  // item; the wire boundary (buildSaveProposalDraftCommand) re-canonicalizes it, keeping the
  // persisted command semantic. Other collections round-trip through their canonical form already.
  const updatedRawById = isDocumentCollectionPath(path)
    ? collectionById(readCollection(updated, path) ?? [], path)
    : undefined;
  const operations: ProposalJSONContentOperation[] = [];

  for (const [itemId, updatedItem] of updatedById) {
    const originalItem = originalById.get(itemId);
    if (originalItem === undefined) {
      operations.push({
        operationId: createId(),
        kind: "CollectionItemAdded",
        path,
        conflictKey: collectionConflictKey(path, itemId),
        itemId,
        value: cloneJson(updatedRawById?.get(itemId) ?? updatedItem)!,
      });
      continue;
    }

    for (const leaf of changedLeaves(originalItem, updatedItem)) {
      operations.push({
        operationId: createId(),
        kind: "CollectionItemUpdated",
        path,
        conflictKey: collectionConflictKey(path, itemId, leaf.path),
        itemId,
        expectedValue: nestedValue(leaf.path, cloneJson(leaf.expectedValue)) as never,
        value: nestedValue(leaf.path, cloneJson(leaf.value)) as never,
      });
    }
  }

  for (const [itemId, originalItem] of originalById) {
    if (updatedById.has(itemId)) continue;
    operations.push({
      operationId: createId(),
      kind: "CollectionItemRemoved",
      path,
      conflictKey: collectionConflictKey(path, itemId),
      itemId,
      expectedValue: cloneJson(originalItem),
    });
  }

  return operations;
}

function collectionById(collection: unknown[], path: string): Map<string, unknown> {
  const entries = collection.map((item) => {
    const itemId = getProposalCollectionItemIdentity(item);
    if (!itemId) throw new Error(`Every collaborative collection item at ${path} requires a stable identity.`);
    return [itemId, item] as const;
  });
  return new Map(entries);
}

function changedLeaves(
  expectedValue: unknown,
  value: unknown,
  path: string[] = [],
): Array<{ path: string[]; expectedValue: unknown; value: unknown }> {
  if (proposalStrictJsonValuesEqual(expectedValue, value)) return [];
  if (path.at(-1) === "teamMember") return [{ path, expectedValue, value }];
  if (!isRecord(expectedValue) || !isRecord(value)) return [{ path, expectedValue, value }];

  const keys = new Set([...Object.keys(expectedValue), ...Object.keys(value)]);
  return (
    [...keys]
      // Omitted keys in the updated object should not be treated as semantic
      // updates. They serialize to undefined (and then {} payloads) rather than
      // an intentional new value.
      .filter((key) => !(value[key] === undefined && expectedValue[key] !== undefined))
      .filter((key) => !identityProperties.has(key))
      .flatMap((key) => changedLeaves(expectedValue[key], value[key], [...path, key]))
  );
}

function nestedValue(path: string[], value: unknown): unknown {
  return path.reduceRight<Record<string, unknown>>((nested, segment) => ({ [segment]: nested }), value as never);
}

function collectionConflictKey(path: string, itemId: string, leafPath: string[] = []): string {
  return [path.slice(1).replaceAll("/", ":"), itemId, ...leafPath].join(":");
}

function reviewerIdentity(reviewer: ProposalReviewerJSON): string {
  return `${reviewer.userId}:${reviewer.legalEntityId}`;
}

function cloneJson(value: unknown): never {
  return (value === undefined ? undefined : structuredClone(value)) as never;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}
